/**
 * Framework-free image helpers for the big-screen dashboard. The venue-map widget
 * stores its floor-plan image inline in localStorage (there is no backend yet), so
 * an uploaded image must be re-encoded down to a size that fits the browser's
 * ~5 MB storage quota before it is persisted.
 */

/** Longest edge, in pixels, an uploaded floor-plan is downscaled to. */
const MAX_EDGE_PX = 1600;
/** JPEG quality used when re-encoding, balancing legibility against size. */
const JPEG_QUALITY = 0.82;

/**
 * Source MIME types that can carry transparency. These are re-encoded as PNG so
 * an alpha channel (e.g. a logo on a transparent background) is preserved —
 * flattening them to JPEG would fill the transparent pixels with black.
 */
const TRANSPARENT_SOURCE_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
]);

/** Raised when an uploaded file cannot be read or decoded as an image. */
export class ImageLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageLoadError';
  }
}

/**
 * Reads an uploaded image file, downscales it so its longest edge is at most
 * {@link MAX_EDGE_PX}, and returns it as a data URL suitable for inline storage.
 * Formats that can carry transparency (PNG, WebP, GIF, SVG) are re-encoded as PNG
 * so their alpha channel survives; opaque formats are re-encoded as JPEG to keep
 * the byte size down. Rejects with an {@link ImageLoadError} when the file is not
 * a decodable image.
 */
export async function downscaleImageToDataUrl(file: File): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight, MAX_EDGE_PX);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new ImageLoadError('Could not get a drawing context to resize the image.');
    }

    // A fresh canvas is transparent black; drawing an image with an alpha channel
    // preserves that transparency, but JPEG has no alpha and would render those
    // pixels black. So keep PNG for transparent sources and only flatten opaque
    // ones to JPEG.
    if (TRANSPARENT_SOURCE_TYPES.has(file.type)) {
      context.drawImage(image, 0, 0, width, height);
      return canvas.toDataURL('image/png');
    }

    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } finally {
    // Always release the blob URL, whether decoding succeeded or failed.
    URL.revokeObjectURL(objectUrl);
  }
}

/** Loads an image element from a URL, rejecting if the source can't be decoded. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new ImageLoadError('The file could not be read as an image.'));
    image.src = src;
  });
}

/**
 * Scales the given dimensions down proportionally so neither edge exceeds
 * `maxEdge`, leaving smaller images untouched. Returns whole pixels.
 */
function fitWithin(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest === 0) {
    return { width, height };
  }
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}
